import { Copy01Icon, Tick02Icon } from '@hugeicons/core-free-icons'
import { HugeiconsIcon } from '@hugeicons/react'
import Prism from 'prismjs'
import * as React from 'react'
import 'prismjs/components/prism-bash'
import 'prismjs/components/prism-json'
import 'prismjs/components/prism-python'
import 'prismjs/components/prism-sql'
import 'prismjs/components/prism-typescript'

import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'

/** Cor de cada tipo de trecho (só tokens de cor do tema). */
const COR_TOKEN: Record<string, string> = {
  comment: 'text-muted-foreground italic',
  string: 'text-success',
  'attr-value': 'text-success',
  keyword: 'text-chart-sem-violet font-medium',
  boolean: 'text-chart-sem-violet',
  number: 'text-info',
  function: 'text-info',
  property: 'text-info',
  punctuation: 'text-muted-foreground',
  operator: 'text-muted-foreground',
}

function renderizar(tokens: (string | Prism.Token)[], prefixo = 't'): React.ReactNode[] {
  return tokens.map((token, i) => {
    const chave = `${prefixo}-${i}`
    if (typeof token === 'string') return <span key={chave}>{token}</span>
    const filhos =
      typeof token.content === 'string'
        ? token.content
        : renderizar(Array.isArray(token.content) ? token.content : [token.content], chave)
    return (
      <span key={chave} className={COR_TOKEN[token.type]}>
        {filhos}
      </span>
    )
  })
}

interface BlocoCodigoProps {
  children: string
  /** python, json, bash, sql, typescript. Outra linguagem aparece como texto puro. */
  linguagem?: string | null
  className?: string
}

/** Bloco de código com cores por linguagem e botão para copiar. */
function BlocoCodigo({ children, linguagem = null, className }: BlocoCodigoProps) {
  const [copiado, setCopiado] = React.useState(false)
  const candidata: unknown = linguagem ? Prism.languages[linguagem] : undefined
  const gramatica =
    candidata && typeof candidata === 'object' ? (candidata as Prism.Grammar) : undefined
  const conteudo = gramatica ? renderizar(Prism.tokenize(children, gramatica)) : children

  async function copiar() {
    try {
      await navigator.clipboard.writeText(children)
      setCopiado(true)
      window.setTimeout(() => setCopiado(false), 1500)
    } catch {
      // Sem permissão para copiar: não faz nada.
    }
  }

  return (
    <div
      data-slot="bloco-codigo"
      className={cn('group/codigo relative rounded-lg bg-muted text-sm', className)}
    >
      <pre className="overflow-x-auto p-4 pr-12 font-mono text-xs leading-relaxed">
        <code>{conteudo}</code>
      </pre>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={copiado ? 'Copiado' : 'Copiar código'}
        onClick={() => void copiar()}
        className="absolute top-2 right-2"
      >
        <HugeiconsIcon icon={copiado ? Tick02Icon : Copy01Icon} strokeWidth={2} />
      </Button>
    </div>
  )
}

export { BlocoCodigo }
